import { memo } from "react";
import PropTypes from "prop-types";
import RelativeLink from "app/pages/.shared/RelativeLink";
import { FormattedMessage } from "react-intl";
import { useAnalytics } from "app/utils/analytics/useAnalytics";
import DotNotification from "app/pages/.shared/DotNotification/DotNotification";
import "./AccountAsideMenuUnauthenticated.scss";
import isEmpty from "lodash/isEmpty";
import Typography, { TYPOGRAPHY_VARIANTS } from "app/pages/.shared/Typography/Typography";
import { HelpCircleFilledIcon } from "app/icons/navigation";
import { AiMessageFilledIcon } from "app/icons/profile";
import { BaggageLFilledIcon } from "app/icons/travel";
import { ICON_SIZES } from "app/constants";

const AccountAsideMenuUnauthenticated = ({
	onItemClick = () => {},
	showCustomerRequestNotification = false,
	myBooking = {},
}) => {
	const { track } = useAnalytics();

	return (
		<div
			className="account-aside-menu-unauthenticated"
			data-testid="account-aside-menu-unauthenticated"
		>
			<div className="account-aside-menu-unauthenticated__body">
				{!isEmpty(myBooking) ? (
					<>
						<div className="account-aside-menu-unauthenticated__list">
							<div className="account-aside-menu-unauthenticated__item">
								<BaggageLFilledIcon size={ICON_SIZES.MD} />
								<Typography variant={TYPOGRAPHY_VARIANTS.LARGE} isBold>
									<FormattedMessage id="menu.item.my.bookings" tagName="h4" />
								</Typography>
							</div>
							<div className="account-aside-menu-unauthenticated__items">
								<RelativeLink
									className="account-aside-menu-unauthenticated__sub-item"
									activeClassName="account-aside-menu-unauthenticated__item--active"
									to={{
										pathname: `/account/mybookings/${
											myBooking.bookingReference
										}`,
									}}
									rel="noopener noreferrer"
									onClick={onItemClick}
									data-testid="go-to-my-booking-link"
								>
									<Typography variant={TYPOGRAPHY_VARIANTS.REGULAR}>
										<FormattedMessage
											id="menu.item.my.booking"
											values={{
												bookingReference: myBooking.bookingReference,
											}}
										/>
									</Typography>
								</RelativeLink>
								<RelativeLink
									className="account-aside-menu-unauthenticated__sub-item"
									activeClassName="account-aside-menu-unauthenticated__item--active"
									to={{ pathname: "/booking-search" }}
									rel="noopener noreferrer"
									onClick={() => {
										track("menu_go_to_search_booking");
										onItemClick();
									}}
									data-testid="go-to-booking-search-link"
								>
									<Typography variant={TYPOGRAPHY_VARIANTS.REGULAR}>
										<FormattedMessage id="menu.mobile.go.to.other.bookings" />
									</Typography>
								</RelativeLink>
							</div>
						</div>
						<div className="account-aside-menu-unauthenticated__list">
							<div className="account-aside-menu-unauthenticated__item">
								<HelpCircleFilledIcon size={ICON_SIZES.MD} />
								<Typography variant={TYPOGRAPHY_VARIANTS.LARGE} isBold>
									<FormattedMessage id="menu.item.need.help" tagName="h4" />
								</Typography>
							</div>
							<div className="account-aside-menu-unauthenticated__items">
								<RelativeLink
									className="account-aside-menu-unauthenticated__sub-item"
									activeClassName="account-aside-menu-unauthenticated__item--active"
									to={{ pathname: "/account/faq-contact" }}
									data-testid="account-aside-menu-unauthenticated-help-item"
									onClick={onItemClick}
								>
									<Typography variant={TYPOGRAPHY_VARIANTS.REGULAR}>
										<FormattedMessage id="menu.item.faq.contact" />
									</Typography>
								</RelativeLink>
								<RelativeLink
									className="account-aside-menu-unauthenticated__sub-item account-aside-menu-unauthenticated__sub-item__chatbot"
									activeClassName="account-aside-menu-unauthenticated__item--active"
									to={{ pathname: "/account/faq-chatbot" }}
									data-testid="account-aside-menu-unauthenticated-faq-chatbot-item"
									onClick={onItemClick}
								>
									<Typography variant={TYPOGRAPHY_VARIANTS.REGULAR}>
										<FormattedMessage id="menu.item.faq.chatbot" />
									</Typography>
									<AiMessageFilledIcon
										width={25}
										height={25}
										useGradient
										gradientId="ai-gradient-chatbot-unauth"
									/>
								</RelativeLink>
								<RelativeLink
									className="account-aside-menu-unauthenticated__sub-item"
									activeClassName="account-aside-menu-unauthenticated__item--active"
									to={{
										pathname: "/account/customer-request/my-requests",
										search: `?bookingReference=${myBooking.bookingReference}`,
									}}
									rel="noopener noreferrer"
									data-testid="account-aside-menu-unauthenticated-my-requests-item"
									onClick={onItemClick}
								>
									<Typography variant={TYPOGRAPHY_VARIANTS.REGULAR}>
										<FormattedMessage id="menu.item.my.requests" />
									</Typography>
									{showCustomerRequestNotification && <DotNotification />}
								</RelativeLink>
							</div>
						</div>
					</>
				) : (
					<>
						<RelativeLink
							className="account-aside-menu-unauthenticated__item"
							activeClassName="account-aside-menu-unauthenticated__item--active"
							to={{ pathname: "/faq" }}
							rel="noopener noreferrer"
							target="_blank"
							data-testid="account-aside-menu-unauthenticated-help-item"
							onClick={onItemClick}
						>
							<HelpCircleFilledIcon size={ICON_SIZES.MD} />
							<Typography variant={TYPOGRAPHY_VARIANTS.LARGE} isBold>
								<FormattedMessage id="menu.item.help" />
							</Typography>
						</RelativeLink>
						<RelativeLink
							className="account-aside-menu-unauthenticated__item"
							activeClassName="account-aside-menu-unauthenticated__item--active"
							to={{ pathname: "/booking-search" }}
							rel="noopener noreferrer"
							onClick={() => {
								track("menu_go_to_search_booking");
								onItemClick();
							}}
							data-testid="go-to-booking-search-link"
						>
							<BaggageLFilledIcon size={ICON_SIZES.MD} />
							<Typography variant={TYPOGRAPHY_VARIANTS.LARGE} isBold>
								<FormattedMessage id="menu.mobile.go.to.my.bookings" tagName="h4" />
							</Typography>
						</RelativeLink>
					</>
				)}
			</div>
		</div>
	);
};

AccountAsideMenuUnauthenticated.propTypes = {
	onItemClick: PropTypes.func,
	showCustomerRequestNotification: PropTypes.bool,
	myBooking: PropTypes.object,
};

export default memo(AccountAsideMenuUnauthenticated);
